import { FormEvent, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Euler, Quaternion, Vector3 } from 'three';
import { ArrowLeft, Eye, EyeOff, RotateCcw } from 'lucide-react';
import { RAD2DEG } from '@constants/MathGeometry';
import { Button, IconButton } from '@/components/ui/button.tsx';
import { Checkbox } from '@/components/ui/checkbox.tsx';
import FormModal from '@/components/ui/FormModal.tsx';
import { SegmentationWorkflowOverlay } from '@segmentation/UI/SegmentationWorkflowOverlay';
import useCorrectionStore from '@correction/hooks/useCorrectionStore.ts';
import { useCreateJawsCorrections } from '@hooks/useJawsCorrection.ts';
import { TransformTool } from '@correction/UI/TransformTool.tsx';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { JawsCorrectionCreate } from '@/schema/JawsCorrection';

const VOP_URL = '/VOP%202025.pdf';

type UploadPhase = 'idle' | 'uploading' | 'success' | 'error';

const FRACTION_DIGITS = 2;

const EYEBROW = 'text-[10px] font-semibold uppercase tracking-[0.16em] text-white/45';
const CARD = 'rounded-xl bg-content-dark/55 p-3';

type AxisKey = 'x' | 'y' | 'z';

type AxisConfig = {
  key: AxisKey;
  label: string;
  caption: string;
  color: string;
};

// Axes are the tooth's orthodontic frame (see ToothCorrection.orthoFrame):
// X mesiodistal, Y long axis (occlusal), Z buccolingual.
const POSITION_AXES: AxisConfig[] = [
  { key: 'x', label: 'X', caption: 'M–D', color: 'bg-red-500' },
  { key: 'y', label: 'Y', caption: 'vert', color: 'bg-green-500' },
  { key: 'z', label: 'Z', caption: 'B–L', color: 'bg-primary-300' },
];

const ROTATION_AXES: AxisConfig[] = [
  { key: 'x', label: 'X', caption: 'incl', color: 'bg-red-500' },
  { key: 'y', label: 'Y', caption: 'rot', color: 'bg-green-500' },
  { key: 'z', label: 'Z', caption: 'ang', color: 'bg-primary-300' },
];

type AxisValueInputProps = {
  value: number;
  step: number;
  disabled: boolean;
  ariaLabel: string;
  onCommit: (value: number) => void;
};

/**
 * Numeric cell of the readout. Keeps a local draft while focused so the
 * per-frame store tick doesn't fight the user's typing; commits on Enter/blur,
 * discards on Escape. The committed value is re-read from the tooth, so the
 * cell always ends up showing what was actually applied (post-clamp).
 */
const AxisValueInput = ({ value, step, disabled, ariaLabel, onCommit }: AxisValueInputProps) => {
  const [draft, setDraft] = useState<string | null>(null);

  const commit = () => {
    if (draft !== null) {
      const parsed = Number.parseFloat(draft);
      if (!Number.isNaN(parsed) && parsed !== value) {
        onCommit(parsed);
      }
    }
    setDraft(null);
  };

  if (disabled) {
    return <div className="mt-1 font-mono text-[13px] leading-none text-white tabular-nums">—</div>;
  }

  return (
    <input
      type="number"
      aria-label={ariaLabel}
      step={step}
      value={draft ?? value.toFixed(FRACTION_DIGITS)}
      onFocus={() => setDraft(value.toFixed(FRACTION_DIGITS))}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.currentTarget.blur();
        } else if (event.key === 'Escape') {
          setDraft(null);
          event.currentTarget.blur();
        }
      }}
      className="mt-1 w-full [appearance:textfield] rounded bg-transparent text-center font-mono text-[13px] leading-none text-white tabular-nums outline-none focus:bg-white/10 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
    />
  );
};

type ReadoutProps = {
  title: string;
  unit: string;
  axes: AxisConfig[];
  values: Record<AxisKey, number>;
  step: number;
  disabled: boolean;
  onReset: () => void;
  onCommit: (axis: AxisKey, value: number) => void;
};

const Readout = ({ title, unit, axes, values, step, disabled, onReset, onCommit }: ReadoutProps) => (
  <div className={CARD}>
    <div className="mb-2 flex items-center justify-between">
      <div className="flex items-baseline gap-1.5">
        <h2 className="text-xs font-semibold text-white/90">{title}</h2>
        <span className="text-[10px] font-medium text-white/35">{unit}</span>
      </div>
      <button
        type="button"
        onClick={onReset}
        disabled={disabled}
        title={`Reset ${title.toLowerCase()}`}
        className="flex h-6 items-center gap-1 rounded-md px-1.5 text-[10px] font-medium uppercase tracking-wide text-white/45 transition-colors hover:bg-white/5 hover:text-white/80 disabled:opacity-40 disabled:hover:bg-transparent"
      >
        <RotateCcw className="size-3" />
        Reset
      </button>
    </div>
    <div className="grid grid-cols-3 gap-1.5">
      {axes.map((axis) => (
        <div key={axis.key} className="rounded-lg bg-content/70 px-1.5 py-1.5 text-center">
          <div className="flex items-center justify-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-white/55">
            <span className={`h-1.5 w-1.5 rounded-full ${axis.color}`} />
            {axis.label}
          </div>
          <AxisValueInput
            value={values[axis.key]}
            step={step}
            disabled={disabled}
            ariaLabel={`${title} ${axis.caption} (${unit})`}
            onCommit={(value) => onCommit(axis.key, value)}
          />
          <div className="mt-0.5 text-[9px] uppercase tracking-wider text-white/30">{axis.caption}</div>
        </div>
      ))}
    </div>
  </div>
);

type VisToggleProps = {
  label: string;
  visible: boolean;
  onClick: () => void;
};

const VisToggle = ({ label, visible, onClick }: VisToggleProps) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={visible}
    className={cn(
      'flex items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 text-xs font-medium transition-colors',
      visible
        ? 'border-white/10 bg-content/70 text-white/85 hover:bg-content'
        : 'border-white/5 bg-transparent text-white/40 hover:text-white/70',
    )}
  >
    {visible ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
    {label}
  </button>
);

type ControlsUIProps = {
  className?: string;
  scanId: string;
};

export const ControlsUI = ({ className, scanId }: ControlsUIProps) => {
  const { jaws, tick, resetToothPos, resetToothRot } = useCorrectionStore();
  const navigate = useNavigate();
  const [upperVisible, setUpperVisible] = useState(true);
  const [lowerVisible, setLowerVisible] = useState(true);
  const [ghostsVisible, setGhostsVisible] = useState(false);
  const [occlusionVisible, setOcclusionVisible] = useState(false);
  const [currentUploadingCycle, setCurrentUploadingCycle] = useState<number | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [uploadPhase, setUploadPhase] = useState<UploadPhase>('idle');
  const { mutateAsync: createBulkCorrections } = useCreateJawsCorrections();

  if (!jaws) {
    return null;
  }

  const activeTooth = jaws.activeTooth;
  const hasActiveTooth = Boolean(activeTooth);
  const activeFDI = activeTooth === null ? '—' : activeTooth.getFDI();

  const movedPosition = useMemo(() => {
    if (!activeTooth) return { x: 0, y: 0, z: 0 };
    return activeTooth.getMovedPositionOrtho();
  }, [activeTooth, tick]);

  const movedRotationDegrees = useMemo(() => {
    if (!activeTooth) return { x: 0, y: 0, z: 0 };

    const q: Quaternion = activeTooth.getMovedRotQuaternionOrtho();
    const euler = new Euler().setFromQuaternion(q, 'XYZ');

    return {
      x: euler.x * RAD2DEG,
      y: euler.y * RAD2DEG,
      z: euler.z * RAD2DEG,
    };
  }, [activeTooth, tick]);

  const commitPosition = (axis: 'x' | 'y' | 'z', value: number) => {
    if (!activeTooth) return;
    const pos = new Vector3(movedPosition.x, movedPosition.y, movedPosition.z);
    pos[axis] = value;
    const rot = new Vector3(movedRotationDegrees.x, movedRotationDegrees.y, movedRotationDegrees.z);
    activeTooth.applyOrthoTransform(pos, rot);
  };

  const commitRotation = (axis: 'x' | 'y' | 'z', value: number) => {
    if (!activeTooth) return;
    const pos = new Vector3(movedPosition.x, movedPosition.y, movedPosition.z);
    const rot = new Vector3(movedRotationDegrees.x, movedRotationDegrees.y, movedRotationDegrees.z);
    rot[axis] = value;
    activeTooth.applyOrthoTransform(pos, rot);
  };

  const totalLowerCycles = jaws.lowerJaw.getTransformIterations().getIterationsCount();
  const totalUpperCycles = jaws.upperJaw.getTransformIterations().getIterationsCount();
  const totalCycles = Math.max(totalLowerCycles, totalUpperCycles);
  const hasCycles = totalCycles > 0;
  const isUploading = uploadPhase === 'uploading';

  const toggleUpper = () => {
    const next = !upperVisible;
    setUpperVisible(next);
    jaws.toggleVisibility('upper', next);
  };

  const toggleLower = () => {
    const next = !lowerVisible;
    setLowerVisible(next);
    jaws.toggleVisibility('lower', next);
  };

  const toggleGhosts = () => {
    const next = !ghostsVisible;
    setGhostsVisible(next);
    jaws.setInitialPoseGhostsVisible(next);
  };

  const toggleOcclusion = () => {
    const next = !occlusionVisible;
    setOcclusionVisible(next);
    jaws.setOcclusionMapEnabled(next);
  };

  const applyCycleState = (cycleIndex: number) => {
    jaws.upperJaw.resetToInitialTransforms();
    jaws.lowerJaw.resetToInitialTransforms();
    if (cycleIndex >= 0) {
      jaws.upperJaw.applyIterationTransformations(cycleIndex);
      jaws.lowerJaw.applyIterationTransformations(cycleIndex);
    }
  };

  const handleConfirm = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!jaws || !hasCycles || !agreedToTerms) return;

    setShowConfirm(false);
    setUploadPhase('uploading');
    try {
      const correctionsData: JawsCorrectionCreate[] = [];
      const upperJawModels: Blob[] = [];
      const lowerJawModels: Blob[] = [];

      for (let cycleIndex = 0; cycleIndex < totalCycles; cycleIndex++) {
        setCurrentUploadingCycle(cycleIndex + 1);
        applyCycleState(cycleIndex);
        const payload = await jaws.buildCorrectionUploadPayload(scanId, cycleIndex + 1);
        correctionsData.push(payload.data);
        upperJawModels.push(payload.upperJawModel);
        lowerJawModels.push(payload.lowerJawModel);
      }

      await createBulkCorrections({
        data: { scanId, corrections: correctionsData },
        upperJawModels,
        lowerJawModels,
      });
      setUploadPhase('success');
      // Take the user one step back to the main screen after a short confirmation.
      setTimeout(() => navigate(-1), 1500);
    } catch (error) {
      console.error('Failed to upload corrections', error);
      const description = error instanceof Error ? error.message : 'Unexpected error occurred';
      toast.error('Correction upload failed', { description });
      setUploadPhase('error');
    } finally {
      setCurrentUploadingCycle(null);
      if (hasCycles) {
        applyCycleState(totalCycles - 1);
      } else {
        jaws.upperJaw.resetToInitialTransforms();
        jaws.lowerJaw.resetToInitialTransforms();
      }
    }
  };

  return (
    <>
      <section
        className={cn(
          'w-[300px] rounded-2xl border border-white/8 bg-content/95 p-3.5 text-white shadow-lg shadow-black/40 backdrop-blur',
          className,
        )}
      >
        <header className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className={EYEBROW}>Active tooth</span>
            <h1 className="text-lg font-semibold leading-tight tracking-tight text-primary-300">
              {hasActiveTooth ? `Tooth ${activeFDI}` : 'No selection'}
            </h1>
          </div>
        </header>

        {!hasActiveTooth && (
          <p className="mt-1 text-xs text-white/45">Select a tooth in the scene to inspect and adjust it.</p>
        )}

        <TransformTool className="mt-3" />

        <div className="mt-3 space-y-2.5">
          <Readout
            title="Position"
            unit="mm"
            axes={POSITION_AXES}
            values={movedPosition}
            step={0.05}
            disabled={!hasActiveTooth}
            onReset={resetToothPos}
            onCommit={commitPosition}
          />
          <Readout
            title="Rotation"
            unit="°"
            axes={ROTATION_AXES}
            values={movedRotationDegrees}
            step={0.5}
            disabled={!hasActiveTooth}
            onReset={resetToothRot}
            onCommit={commitRotation}
          />

          <div className={CARD}>
            <p className={cn(EYEBROW, 'mb-2')}>View</p>
            <div className="grid grid-cols-2 gap-1.5">
              <VisToggle label="Upper" visible={upperVisible} onClick={toggleUpper} />
              <VisToggle label="Lower" visible={lowerVisible} onClick={toggleLower} />
              <VisToggle label="Ghost" visible={ghostsVisible} onClick={toggleGhosts} />
              <VisToggle label="Occlusion" visible={occlusionVisible} onClick={toggleOcclusion} />
            </div>
            {occlusionVisible && (
              <div className="mt-2">
                <div
                  className="h-1.5 w-full rounded-full"
                  style={{
                    background:
                      'linear-gradient(to right, #8c0000, #ff0000, #ffff00, #00ff00, #ffffff)',
                  }}
                />
                <div className="mt-0.5 flex justify-between text-[9px] uppercase tracking-wider text-white/40">
                  <span>hit</span>
                  <span>contact</span>
                  <span>1 mm</span>
                  <span>2 mm</span>
                </div>
              </div>
            )}
          </div>

          <div className={CARD}>
            <div className="mb-2 flex items-center justify-between">
              <p className={EYEBROW}>Treatment plan</p>
              <span className="text-[10px] font-medium text-white/40">
                {hasCycles ? `${totalCycles} cycle${totalCycles === 1 ? '' : 's'}` : 'No cycles'}
              </span>
            </div>
            <Button
              variant="default"
              size="sm"
              type="button"
              onClick={() => setShowConfirm(true)}
              disabled={isUploading || !hasCycles}
              className="w-full"
            >
              Confirm treatment plan
            </Button>
          </div>
        </div>
      </section>

      {showConfirm && (
        <FormModal
          submitText="Confirm treatment plan"
          submitButton="default"
          cancelText="Back"
          submitDisabled={!agreedToTerms}
          onClose={() => setShowConfirm(false)}
          onSubmit={handleConfirm}
        >
          <div className="space-y-4 text-white">
            <h2 className="text-lg font-semibold">Confirm treatment plan</h2>
            <p className="text-sm text-white/70">
              Are you sure you want to confirm the treatment plan? This will submit{' '}
              {totalCycles} treatment cycle{totalCycles === 1 ? '' : 's'}.
            </p>
            <label className="flex items-start gap-3 text-sm text-white/80">
              <Checkbox
                className="mt-0.5"
                checked={agreedToTerms}
                onCheckedChange={(value) => setAgreedToTerms(value === true)}
              />
              <span>
                I agree with the{' '}
                <a
                  href={VOP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  Terms and Conditions (VOP 2025)
                </a>
                .
              </span>
            </label>
          </div>
        </FormModal>
      )}

      <SegmentationWorkflowOverlay
        open={uploadPhase === 'uploading'}
        eyebrow="Submitting treatment plan"
        title="Uploading treatment plan"
        message={
          currentUploadingCycle !== null
            ? `Uploading cycle ${currentUploadingCycle} / ${totalCycles}`
            : 'Finalizing your case'
        }
        variant="loading"
      />
      <SegmentationWorkflowOverlay
        open={uploadPhase === 'success'}
        title="Treatment plan confirmed"
        message="Returning back in a moment…"
        variant="success"
      />
      <SegmentationWorkflowOverlay
        open={uploadPhase === 'error'}
        title="Upload failed"
        message="Something went wrong while submitting. Try again."
        variant="error"
        footer={
          <IconButton
            className="mt-2 w-full rounded-2xl border border-red-400/40 bg-red-500/20"
            variant="ghost-danger"
            icon={ArrowLeft}
            onClick={() => setUploadPhase('idle')}
          >
            Back
          </IconButton>
        }
      />
    </>
  );
};
